import { auth } from '@/config/firebase';
import type { AppliedSavedModel, JobEntity } from '@/types/job.types';

/**
 * Applied / saved status cache — mirrors Flutter LocalDataService.getAppliedSavedJobs /
 * putAppliedSavedJobs (key `appliedSavedJobs`). Flutter writes it on every successful
 * `GET candidate/applied-saved-jobs` and applies it to freshly loaded jobs before the network
 * result arrives (WebHomeBloc / ExtractedResumeBloc `_GetSavedAppliedJobs`).
 */

const APPLIED_SAVED_KEY = 'appliedSavedJobs';

/** The cached response, or null when signed out (Flutter returns null without a user). */
export function readAppliedSavedCache(): AppliedSavedModel | null {
  if (!auth.currentUser) return null;
  try {
    const json = localStorage.getItem(APPLIED_SAVED_KEY);
    return json ? (JSON.parse(json) as AppliedSavedModel) : null;
  } catch {
    return null;
  }
}

export function writeAppliedSavedCache(data: AppliedSavedModel): void {
  try {
    localStorage.setItem(APPLIED_SAVED_KEY, JSON.stringify(data));
  } catch {
    // Storage full or blocked — the network result still applies
  }
}

/**
 * Returns the entities with the applied / easy-applied / saved flags from `data`.
 * Flutter GenericHelpers.updateAppliedSavedJobs only ever sets flags to true; here the network
 * result is authoritative, so a stale cached flag is cleared when the server no longer has it.
 */
export function withAppliedSaved(entities: JobEntity[], data: AppliedSavedModel | null | undefined): JobEntity[] {
  if (!data?.appliedSavedJobs) return entities;
  const applied = new Set(data.appliedSavedJobs.Applied_Jobs ?? []);
  const easyApplied = new Set(data.appliedSavedJobs.EasyApplied_Jobs ?? []);
  const saved = new Set(data.appliedSavedJobs.Saved_Jobs ?? []);
  return entities.map((e) =>
    e.displayId
      ? {
          ...e,
          isApplied: applied.has(e.displayId),
          isEasyApplied: easyApplied.has(e.displayId),
          isSavedJob: saved.has(e.displayId),
        }
      : e,
  );
}
